import { makeAutoObservable } from 'mobx'
import { type MouseEvent } from 'react'
import { type Blocker } from 'react-router'
import { AxiosError } from 'axios'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { type ModalType, ModalTypeList } from 'shared/ui/Modal/store/types'
import { toastStore } from 'shared/ui'
import { ZIndex } from 'shared/constants/zIndex'
import { type IResponseFilterSegment } from 'entities/Segment'
import { type Segment } from 'entities/Segment/model/Segment'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ModalContent } from '../ui/ModalContent'
import { EditSegmentActions } from '../ui/EditSegmentActions'
import { NewSegmentActions } from '../ui/NewSegmentActions'
import { ModalNewSegmentError } from '../ui/ModalNewSegmentError'

type IPropGetAskIfSaveModal = {
  onToggle: () => void
  blocker?: Blocker
  disabled?: boolean
  noSetActive?: boolean
  filtersList: IResponseFilterSegment[]
}

type IOpenAskIfSaveModal = {
  onToggle: () => void
  filtersList: IResponseFilterSegment[]
  noSetActive?: boolean
}

type ISegmentModalStoreConfig = {
  setSegment?: (segment: Segment) => void
  onActionEdit?: () => void
  onActionNew?: () => void
}

export class SegmentModalStore {
  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  private _idModal = ''
  private _name = ''
  private _loading = false
  private _segment: Segment | null = null
  private _config: ISegmentModalStoreConfig | null = null
  private _segmentFilter: IResponseFilterSegment[] = []

  setSegmentFilter = (value: IResponseFilterSegment[]) => {
    this._segmentFilter = value
  }

  setConfig = (config: ISegmentModalStoreConfig) => {
    this._config = config
  }

  setSegment = (segment: Segment | null) => {
    this._segment = segment
  }

  onActionEdit = () => {
    this._config?.onActionEdit?.()
  }

  onActionNew = () => {
    this._config?.onActionNew?.()
  }

  get config() {
    return this._config
  }

  get idModal() {
    return this._idModal
  }

  get segmentName() {
    return this._name
  }

  get disabledCreating() {
    const segment = this._segment
    const onlySpacesInName = /^\s*$/.test(this.segmentName)
    const noEditedName = segment?.name === this.segmentName

    return this.segmentName.length < 1 || onlySpacesInName || noEditedName || this.isNameLengthError
  }

  get isNameLengthError() {
    return this._name.length > 100
  }

  getAskIfSaveModal = ({
    onToggle,
    blocker,
    disabled,
    noSetActive = false,
    filtersList,
  }: IPropGetAskIfSaveModal) => {
    this._idModal = 'askSaveSegment_modal'
    const segment = this._segment

    return {
      disabledOnAllClose: true,
      type: 'warning' as ModalType['type'],
      title: `Save changes to segment “${segment?.name}” before closing?`,
      id: 'askSaveSegment_modal',
      zIndex: ZIndex.OVERLAY_MIDDLE,
      primaryAction: {
        text: 'Save changes',
        disabled: disabled,
        onAction: (e?: MouseEvent<HTMLButtonElement>) => {
          e?.stopPropagation()
          const segment = this._segment
          this._name = segment?.name || ''

          segment?.id === -1 ? this.createItem(filtersList) : this.editItem({ noSetActive })
          modalStore.closeModal(this._idModal)
          onToggle()
          blocker?.proceed && blocker?.proceed()
        },
      },
      additionalSecondaryAction: {
        text: 'Don’t save',
        onAction: (e?: MouseEvent<HTMLButtonElement>) => {
          e?.stopPropagation()
          onToggle()
          modalStore.closeModal(this._idModal)
          blocker?.proceed && blocker?.proceed()
        },
      },
      secondaryAction: {
        text: 'Cancel',
        onAction: (e?: MouseEvent<HTMLButtonElement>) => {
          e?.stopPropagation()
          modalStore.closeModal(this._idModal)
          blocker?.reset && blocker?.reset()
        },
      },
    }
  }

  openAskIfSaveModal = (props: IOpenAskIfSaveModal) => {
    modalStore.addModal(this.getAskIfSaveModal(props))
  }

  openNewSegmentModal = () => {
    this._idModal = 'newSegment_modal'

    modalStore.addModal({
      id: this._idModal,
      title: 'New segment',
      showHeader: true,
      showCloseButton: false,
      showCloseIcon: false,
      width: 480,
      zIndex: ZIndex.OVERLAY_MIDDLE,
      onClose: this.closeModal,
      ModalContentProps: {
        segmentModalStore: this,
      },
      ModalActions: NewSegmentActions,
      ModalContent: ModalContent,
      ModalBottomError: ModalNewSegmentError,
    })
  }

  openRenameSegmentModal = (name: string) => {
    this._idModal = 'renameSegment_modal'
    this._name = name

    modalStore.addModal({
      id: this._idModal,
      showHeader: true,
      showCloseButton: false,
      showCloseIcon: false,
      width: 480,
      zIndex: ZIndex.OVERLAY_MIDDLE,
      onClose: this.closeModal,
      ModalContentProps: {
        segmentModalStore: this,
      },
      title: 'Rename',
      paddingContent: '0 24px 24px 24px',
      ModalActions: EditSegmentActions,
      ModalContent: ModalContent,
      ModalBottomError: ModalNewSegmentError,
    })
  }

  openDeleteSegmentModal = (name: string, deleteHandler: () => void) => {
    this._idModal = 'deleteSegment_modal'
    this._name = name

    const titleName = name.length > 20 ? name.slice(0, 20) + '...' : name

    modalStore.addModal({
      id: this._idModal,
      showHeader: true,
      showCloseButton: false,
      showCloseIcon: false,
      width: 480,
      zIndex: ZIndex.OVERLAY_MIDDLE,
      onClose: this.closeModal,
      ModalContentProps: {
        segmentModalStore: this,
      },
      title: `Delete “${titleName}” segment?`,
      desc: 'This action cannot be undone',
      type: ModalTypeList.ALERT,
      secondaryAction: {
        text: 'Cancel',
        onAction: this.closeModal,
      },
      primaryAction: {
        text: 'Delete',
        onAction: deleteHandler,
      },
    })
  }

  deleteSegment = async (id: number) => {
    await this._entities.segmentsStore.deleteItem(id)

    toastStore.add({
      title: 'Segment deleted',
      type: 'success',
    })
  }

  closeModal = () => {
    this._name = ''

    modalStore.removeModal(this._idModal)
  }

  handleChangeValue = (value: string) => {
    this._name = value
  }

  createItem = async (filtersList: IResponseFilterSegment[]) => {
    try {
      const newSegment = await this._entities.segmentsStore.createItem({
        name: this._name,
        filters: {
          filtersList: filtersList ?? [],
        },
      })
      this._config?.setSegment?.(newSegment)

      toastStore.add({
        type: 'success',
        title: 'Segment created',
      })
    } catch (e) {
      this.errorHandler(e)
    }
  }

  errorHandler = (error: unknown) => {
    if (error instanceof AxiosError) {
      toastStore.add({
        title: error.errors.getError('name') || error.message,
        type: 'error',
      })
    }
  }

  editItem = async ({
    noSetActive,
    afterEdit,
  }: {
    noSetActive?: boolean
    afterEdit?: () => void
  }) => {
    const segment = this._segment
    const segmentFilter = this._segmentFilter
    if (!segment) return

    try {
      const updatedSegment = await this._entities.segmentsStore.updateItem(segment.id, {
        filters: {
          filtersList: segmentFilter,
        },
        name: this._name.length ? this._name : segment.name,
      })

      toastStore.add({
        title: 'Segment saved',
        type: 'success',
      })

      if (!noSetActive && updatedSegment) this._config?.setSegment?.(updatedSegment)

      afterEdit?.()
    } catch (e) {
      this.errorHandler(e)
    }
  }
}
